import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import type { IntakeHubDashboardResponse } from '@store/generated/intake-hub';

import { getProgressForService, splitProfileItems } from './domain';
import type {
  IntakeBriefingAction,
  IntakeDashboardScenario,
  IntakeItem,
  IntakeItemOwner,
  IntakeItemStatus,
  IntakeItemType,
  IntakeServiceId,
  IntakeSourceRef,
  IntakeSourceRefType,
  IntakeSourceRelationship,
  IntakeSubjectType,
  PayerProductStatus,
  PayerStatus,
} from './types';

const SERVICE_IDS = new Set<IntakeServiceId>(['jotcred', 'jotbill', 'jotsite']);
const ITEM_STATUSES = new Set<IntakeItemStatus>([
  'missing',
  'submitted',
  'ai_flagged',
  'in_review',
  'accepted',
  'expiring',
  'needs_attention',
  'staff_in_progress',
  'staff_complete',
  'not_applicable',
]);
const ITEM_TYPES = new Set<IntakeItemType>([
  'information',
  'document',
  'action',
  'staff_task',
  'review',
]);
const ITEM_OWNERS = new Set<IntakeItemOwner>(['clinician', 'practice_admin', 'jotpsych']);
const SUBJECT_TYPES = new Set<IntakeSubjectType>(['provider', 'organization', 'service', 'payer']);
const PAYER_STATUSES = new Set<PayerProductStatus>([
  'not_started',
  'needs_info',
  'in_progress',
  'submitted',
  'approved',
  'active',
]);

const EMPTY_PROVIDER = {
  name: 'Intake Hub',
  credentialType: 'Provider intake',
  subtitle: 'No intake requirements are assigned yet.',
} as const;

const SERVICE_LABELS: Record<IntakeServiceId, { label: string; description: string }> = {
  jotcred: {
    label: 'JotCred',
    description: 'Credentialing intake and payer application support.',
  },
  jotbill: {
    label: 'JotBill',
    description: 'Billing setup, payer readiness, and operational onboarding.',
  },
  jotsite: {
    label: 'JotSite',
    description: 'Practice website launch readiness.',
  },
};

const EMPTY_DASHBOARD: IntakeDashboardScenario = {
  activeServiceId: 'jotcred',
  provider: {
    ...EMPTY_PROVIDER,
    neededItems: [],
    onFileItems: [],
  },
  services: [],
  briefingActions: [],
  serviceItems: {
    jotcred: [],
    jotbill: [],
    jotsite: [],
  },
  payerStatuses: {
    jotcred: [],
    jotbill: [],
    jotsite: [],
  },
};

function stringValue(value: unknown, fallback = '') {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback;
}

function serviceIdOrUndefined(value: unknown): IntakeServiceId | undefined {
  return typeof value === 'string' && SERVICE_IDS.has(value as IntakeServiceId)
    ? (value as IntakeServiceId)
    : undefined;
}

function serviceId(value: unknown, fallback: IntakeServiceId = 'jotcred'): IntakeServiceId {
  return serviceIdOrUndefined(value) ?? fallback;
}

function itemStatus(value: unknown): IntakeItemStatus {
  return typeof value === 'string' && ITEM_STATUSES.has(value as IntakeItemStatus)
    ? (value as IntakeItemStatus)
    : 'missing';
}

function itemType(value: unknown): IntakeItemType {
  return typeof value === 'string' && ITEM_TYPES.has(value as IntakeItemType)
    ? (value as IntakeItemType)
    : 'document';
}

function itemOwner(value: unknown): IntakeItemOwner {
  return typeof value === 'string' && ITEM_OWNERS.has(value as IntakeItemOwner)
    ? (value as IntakeItemOwner)
    : 'clinician';
}

function subjectType(value: unknown): IntakeSubjectType {
  return typeof value === 'string' && SUBJECT_TYPES.has(value as IntakeSubjectType)
    ? (value as IntakeSubjectType)
    : 'provider';
}

function payerStatus(value: unknown): PayerProductStatus {
  return typeof value === 'string' && PAYER_STATUSES.has(value as PayerProductStatus)
    ? (value as PayerProductStatus)
    : 'not_started';
}

function objectArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object',
      )
    : [];
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : [];
}

function toSourceRef(raw: Record<string, unknown>): IntakeSourceRef {
  return {
    type: stringValue(raw.type) as IntakeSourceRefType,
    id: stringValue(raw.id),
    path: stringValue(raw.path, undefined),
    relationship: stringValue(raw.relationship) as IntakeSourceRelationship,
    label: stringValue(raw.label, undefined),
    observedAt: stringValue(raw.observed_at, undefined),
  };
}

function toItem(raw: Record<string, unknown>): IntakeItem {
  const rawServiceIds = Array.isArray(raw.service_ids) ? raw.service_ids : [raw.service_key];
  const serviceIds = rawServiceIds.filter(Boolean).flatMap(id => {
    const parsed = serviceIdOrUndefined(id);
    return parsed ? [parsed] : [];
  });

  return {
    id: stringValue(raw.intake_item_id, stringValue(raw.id, stringValue(raw.requirement_key))),
    intakeItemId: stringValue(raw.intake_item_id, undefined),
    requirementKey: stringValue(raw.requirement_key, stringValue(raw.id)),
    conceptKey: stringValue(raw.concept_key, undefined),
    label: stringValue(raw.title, stringValue(raw.label, 'Intake item')),
    description: stringValue(raw.description, undefined),
    status: itemStatus(raw.status),
    type: itemType(raw.item_type ?? raw.type),
    owner: itemOwner(raw.owner),
    subjectType: subjectType(raw.subject_type),
    subjectRef:
      raw.subject_ref && typeof raw.subject_ref === 'object'
        ? (raw.subject_ref as Record<string, unknown>)
        : null,
    serviceIds,
    sourceRefs: objectArray(raw.source_refs).map(toSourceRef),
    evidenceSummary:
      raw.evidence_summary && typeof raw.evidence_summary === 'object'
        ? (raw.evidence_summary as Record<string, unknown>)
        : {},
    expectedFields:
      raw.metadata && typeof raw.metadata === 'object'
        ? stringArray((raw.metadata as Record<string, unknown>).expected_fields)
        : [],
    actionLabel: stringValue(raw.action_label, undefined),
    instruction: stringValue(raw.instruction, undefined),
    issueNote: stringValue(raw.issue_note, undefined),
    updatedAtLabel: stringValue(raw.updated_at_label, undefined),
  };
}

function toBriefingAction(raw: Record<string, unknown>, index: number): IntakeBriefingAction {
  const tone = stringValue(raw.tone);
  const status = itemStatus(raw.status);
  const Icon =
    status === 'expiring' || tone === 'warning'
      ? ErrorOutlineOutlinedIcon
      : tone === 'success'
        ? AssignmentTurnedInOutlinedIcon
        : stringValue(raw.requirement_key).includes('key')
          ? KeyOutlinedIcon
          : UploadFileOutlinedIcon;

  return {
    id: stringValue(raw.id, `briefing-${index}`),
    title: stringValue(raw.title, stringValue(raw.label, 'Next intake item')),
    description: stringValue(raw.description, ''),
    serviceId: serviceId(raw.service_key ?? raw.service_id),
    tone:
      tone === 'warning' || tone === 'error' || tone === 'success'
        ? tone
        : status === 'needs_attention'
          ? 'error'
          : status === 'expiring' || status === 'ai_flagged'
            ? 'warning'
            : 'default',
    icon: Icon,
    actionLabel: stringValue(raw.action_label, 'Open'),
  };
}

function toPayer(raw: Record<string, unknown>): PayerStatus {
  return {
    id: stringValue(raw.id, stringValue(raw.payer_id, stringValue(raw.name))),
    name: stringValue(raw.name, stringValue(raw.display_name, 'Payer')),
    product: serviceId(raw.product ?? raw.service_key),
    status: payerStatus(raw.status),
    note: stringValue(raw.note, stringValue(raw.description)),
    effectiveDate: stringValue(raw.effective_date, undefined),
    warning: stringValue(raw.warning, undefined),
  };
}

export function intakeHubDashboardResponseToScenario(
  response: IntakeHubDashboardResponse | undefined,
): IntakeDashboardScenario {
  if (!response) {
    return EMPTY_DASHBOARD;
  }

  const items = objectArray(response.items).map(toItem);
  const services = objectArray(response.services).map((service, index) => {
    const id = serviceId(service.service_key ?? service.id);
    const serviceItems = items.filter(item => item.serviceIds.includes(id));
    const serviceFallback = SERVICE_LABELS[id];
    return {
      id,
      label: stringValue(service.label, serviceFallback.label),
      description: stringValue(service.description, serviceFallback.description),
      progress:
        typeof service.progress === 'number'
          ? service.progress
          : getProgressForService(serviceItems),
      active: service.status !== 'deprecated',
      accent: index === 1 ? 'secondary' : index === 2 ? 'warning' : 'primary',
    } satisfies IntakeDashboardScenario['services'][number];
  });
  const activeServiceId = services[0]?.id ?? 'jotcred';
  const profileItems = items.filter(item => item.subjectType === 'provider');
  const { neededItems, onFileItems } = splitProfileItems(profileItems);
  const serviceItems = {
    jotcred: items.filter(item => item.serviceIds.includes('jotcred')),
    jotbill: items.filter(item => item.serviceIds.includes('jotbill')),
    jotsite: items.filter(item => item.serviceIds.includes('jotsite')),
  };
  const payerTiles = objectArray(response.payer_tiles).map(toPayer);
  const clinician = response.clinician as Record<string, unknown>;
  const profileSummary = response.profile_summary as Record<string, unknown>;

  return {
    provider: {
      name: stringValue(clinician.name, EMPTY_PROVIDER.name),
      credentialType: stringValue(clinician.credential_type, EMPTY_PROVIDER.credentialType),
      subtitle: stringValue(profileSummary.subtitle, EMPTY_PROVIDER.subtitle),
      neededItems,
      onFileItems,
    },
    services,
    activeServiceId,
    briefingActions: objectArray(response.briefing_items).map(toBriefingAction),
    serviceItems,
    payerStatuses: {
      jotcred: payerTiles.filter(payer => payer.product === 'jotcred'),
      jotbill: payerTiles.filter(payer => payer.product === 'jotbill'),
      jotsite: payerTiles.filter(payer => payer.product === 'jotsite'),
    },
  };
}
